import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Repository } from '../../types';
import { useAppStore } from '../../store';
import apiService from '../../services/api';

interface ProjectCardProps {
  project: Repository;
}

const ProjectCard: React.FC<ProjectCardProps> = ({ project }) => {
  const { isAuthenticated, user, toggleFavorite, isFavorited } = useAppStore();
  const navigate = useNavigate();
  const [showFullDescription, setShowFullDescription] = useState(false);

  // 检查项目是否已收藏
  const isProjectFavorited = isFavorited(project.full_name);

  // 检查是否可以使用AI解读功能
  // 逻辑：已登录 且 （用户已配置 LLM Key 或 被标记为付费用户）
  const canUseAI = isAuthenticated && (user?.has_qianwen_key || user?.is_paid);

  const handleAnalyze = async () => {
    if (!isAuthenticated) {
      alert('请先登录后再使用AI解读功能');
      return;
    }

    if (!user?.is_paid && !user?.has_qianwen_key) {
      alert('请先在个人设置中配置 LLM 模型接口，或联系管理员开通付费用户后再使用 AI 解读功能');
      return;
    }

    try {
      // 先检查缓存
      const cacheResult = await apiService.checkProjectCache(project);

      if (cacheResult.success && cacheResult.has_cache) {
        // 有缓存，直接跳转到结果页面并传递缓存数据
        const [owner, repo] = project.full_name.split('/');
        navigate(`/project-analysis/${owner}/${repo}`, {
          state: {
            project,
            cachedAnalysis: cacheResult.cache_data,
            fromCache: true
          }
        });
      } else {
        // 无缓存，跳转到分析页面
        const [owner, repo] = project.full_name.split('/');
        navigate(`/project-analysis/${owner}/${repo}`, { state: { project } });
      }
    } catch (error) {
      console.error('检查缓存失败:', error);
      // 如果检查缓存失败，仍然跳转到分析页面
      const [owner, repo] = project.full_name.split('/');
      navigate(`/project-analysis/${owner}/${repo}`, { state: { project } });
    }
  };

  const handleToggleFavorite = async () => {
    if (!isAuthenticated) {
      alert('请先登录后再使用收藏功能');
      return;
    }

    // 调试信息：检查数据完整性
    console.log('🔍 收藏数据检查 (搜索结果):', {
      name: project.name,
      full_name: project.full_name,
      url: project.url,
      description: project.description,
      stars: project.stars,
      language: project.language
    });

    // 检查必需字段
    if (!project.url) {
      console.error('❌ 项目URL缺失:', project);
      alert('项目数据不完整，无法收藏');
      return;
    }

    await toggleFavorite(project);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN');
  };

  const handleDescriptionClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowFullDescription(!showFullDescription);
  };





  return (
    <div className="project-card">
      {/* 项目头部区域 - 独立紫色背景区域 */}
      <div className="project-header-section">
        <h3 className="project-title">
          <a href={project.url || project.html_url} target="_blank" rel="noopener noreferrer">
            {project.name}
          </a>
        </h3>

        <div className="project-info-row">
          <div className="project-author">
            <a href={`https://github.com/${typeof project.owner === 'string' ? project.owner : project.owner?.login}`} target="_blank" rel="noopener noreferrer">
              {typeof project.owner === 'string' ? project.owner : project.owner?.login}
            </a>
          </div>
          <div className="project-stats">
            <div className="stat-item">
              <i className="fas fa-star"></i>
              <span>{(project.stars || project.stargazers_count || 0).toLocaleString()}</span>
            </div>
            <div className="stat-item">
              <i className="fas fa-code-branch"></i>
              <span>{(project.forks || project.forks_count || 0).toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="project-content">

        {/* 项目描述区域 - 占据主要空间 */}
        <div className="project-description-section">
          <div
            className={`project-description ${showFullDescription ? 'expanded' : ''}`}
            onClick={handleDescriptionClick}
          >
            {project.description || '暂无描述'}
          </div>
          {project.description && project.description.length > 100 && (
            <div className="description-expand-btn" onClick={handleDescriptionClick}>
              {showFullDescription ? '收起' : '...更多'}
            </div>
          )}
        </div>

        {/* 项目底部区域 - 合并语言和操作信息 */}
        <div className="project-footer-section">
          {/* 编程语言行 */}
          {project.language && (
            <div className="project-languages">
              <span className="meta-item language">
                <span className="language-dot" style={{ backgroundColor: getLanguageColor(project.language) }}></span>
                {project.language}
              </span>
            </div>
          )}

          {/* 时间和操作按钮行 */}
          <div className="project-footer">
            <div className="project-updated">
              <i className="fas fa-history"></i>
              更新于 {formatDate(project.updated_at)}
            </div>

            {/* 右下角操作按钮 */}
            <div className="project-actions">
              <button
                className={`action-btn-small ${isProjectFavorited ? 'favorited' : ''}`}
                onClick={handleToggleFavorite}
                title={isProjectFavorited ? '取消收藏' : '收藏项目'}
                disabled={!isAuthenticated}
              >
                <i className="fas fa-heart"></i>
              </button>
              <button
                className="action-btn-small"
                onClick={handleAnalyze}
                disabled={!canUseAI}
                title={
                  !isAuthenticated
                    ? "请先登录后使用AI解读功能"
                    : !canUseAI
                      ? "请先在个人设置中配置 LLM 模型接口，或联系管理员开通付费用户"
                      : "AI解读"
                }
              >
                <i className="fas fa-robot"></i>
              </button>
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};

// 获取编程语言对应的颜色
const getLanguageColor = (language: string): string => {
  const colors: { [key: string]: string } = {
    'JavaScript': '#f1e05a',
    'TypeScript': '#2b7489',
    'Python': '#3572A5',
    'Java': '#b07219',
    'C++': '#f34b7d',
    'C': '#555555',
    'C#': '#239120',
    'PHP': '#4F5D95',
    'Ruby': '#701516',
    'Go': '#00ADD8',
    'Rust': '#dea584',
    'Swift': '#ffac45',
    'Kotlin': '#F18E33',
    'Dart': '#00B4AB',
    'HTML': '#e34c26',
    'CSS': '#1572B6',
    'Vue': '#4FC08D',
    'React': '#61DAFB',
    'Shell': '#89e051',
    'Dockerfile': '#384d54',
    'Jupyter Notebook': '#DA5B0B'
  };

  return colors[language] || '#586069';
};

export default ProjectCard;
